// A session's events on a line (brand v2): see Orbit below. labelFractions keeps close ticks'
// numbers readable.

export interface OrbitTick {
  fraction: number
  /** For a hang: where it ends on the orbit. */
  endFraction?: number
}

/**
 * Where each tick's number goes: at the tick, nudged along the orbit so close ticks keep
 * readable labels. The ticks themselves stay where their time puts them.
 */
export function labelFractions(fractions: number[], gap = 0.08): number[] {
  const out = [...fractions]
  for (let i = 1; i < out.length; i++)
    out[i] = Math.max(out[i] as number, (out[i - 1] as number) + gap)
  const over = (out.at(-1) ?? 0) - 1
  if (over > 0) {
    out[out.length - 1] = 1
    for (let i = out.length - 2; i >= 0; i--)
      out[i] = Math.min(out[i] as number, (out[i + 1] as number) - gap)
  }
  return out.map((f) => Math.min(1, Math.max(0, f)))
}

/**
 * The session as a recording (brand v2, D121: the session recorded on the phone, not an orbit):
 * one line from the start to the last event, the events as numbered ticks, a hang as a thick
 * segment in the degraded color, the crash (`periapsis`) as the one accent, and the selected
 * event's time and name above. The name stays for its callers (Home, Session, Get started).
 */
export function Orbit({
  ticks,
  hangs,
  periapsis,
  selected,
  deviceLabel,
  centerTitle,
  centerSub,
}: {
  ticks: OrbitTick[]
  hangs: OrbitTick[]
  periapsis: number
  selected: number
  deviceLabel: string
  centerTitle?: string
  centerSub?: string
}) {
  const W = 580
  const L = 24
  const R = W - 24
  const Y = 104
  const x = (f: number) => L + (R - L) * Math.min(1, Math.max(0, f))
  const labels = labelFractions(
    ticks.map((t) => t.fraction),
    0.045,
  )
  const dotX = x(ticks[selected]?.fraction ?? 1)
  const periX = ticks[periapsis] ? x(ticks[periapsis].fraction) : undefined
  const end = x(Math.max(...ticks.map((t) => t.fraction), 0))
  return (
    <svg className="orbit tl" viewBox={`0 0 ${W} 150`} aria-hidden="true">
      {centerTitle ? (
        <text className="o-time" x={L} y={40} textAnchor="start">
          {centerTitle}
        </text>
      ) : null}
      {centerSub ? (
        <text className="o-name" x={L} y={62} textAnchor="start">
          {centerSub.length > 64 ? `${centerSub.slice(0, 63)}…` : centerSub}
        </text>
      ) : null}
      <text className="o-label o-dev" x={R} y={40} textAnchor="end">
        {deviceLabel}
      </text>
      <line className="o-after" x1={end} x2={R} y1={Y} y2={Y} />
      <line className="o-path" x1={L} x2={end} y1={Y} y2={Y} />
      {hangs.map((h) => (
        <line
          key={`${h.fraction}-${h.endFraction}`}
          className="o-hang"
          x1={x(h.fraction)}
          x2={Math.max(x(h.fraction) + 2, x(h.endFraction ?? h.fraction))}
          y1={Y}
          y2={Y}
        />
      ))}
      {ticks.map((t, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: ticks are positional
        <g key={i}>
          <circle className="o-tick" cx={x(t.fraction)} cy={Y} r="4.5" />
          <text className="o-label" x={x(labels[i] ?? t.fraction)} y={Y + 30}>
            {i + 1}
          </text>
        </g>
      ))}
      {periX !== undefined ? <circle className="o-ring" cx={periX} cy={Y} r="12" /> : null}
      <circle
        className="o-dot orbit-dot"
        cx="0"
        cy="0"
        r="7"
        style={{ transform: `translate(${dotX}px, ${Y}px)` }}
      />
    </svg>
  )
}
